/**
 * ContextMenu — Apple-style dropdown menu for card actions.
 *
 * Portal-based, auto-positioned below anchor (clamped to viewport).
 * Escape key + scroll/wheel dismiss. Fully built from ds tokens.
 *
 * Usage:
 *   <ContextMenu isOpen={open} onClose={() => setOpen(false)} anchorEl={ref.current}>
 *     <ContextMenuItem icon={<Pencil />} label="Edit" onClick={handleEdit} />
 *     <ContextMenuItem icon={<Trash2 />} label="Delete" onClick={handleDelete} variant="danger" />
 *   </ContextMenu>
 */

import { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ds } from '../tokens';

/* ─── Container ─────────────────────────────────────────────── */

interface ContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
  children: React.ReactNode;
}

export function ContextMenu({ isOpen, onClose, anchorEl, children }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Position below anchor, right-aligned
  useLayoutEffect(() => {
    if (!isOpen || !anchorEl) { setCoords(null); return; }
    const rect = anchorEl.getBoundingClientRect();
    const menuWidth = ds.layout.contextMenu.widthPx;
    const gap = 4;
    let left = rect.right - menuWidth;
    if (left < gap) left = gap;
    setCoords({ top: rect.bottom + gap, left });
  }, [isOpen, anchorEl]);

  // Clamp to viewport after portal mounts
  useLayoutEffect(() => {
    if (!coords || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const pad = 4;
    if (coords.top + rect.height > window.innerHeight - pad) {
      menuRef.current.style.top = `${window.innerHeight - pad - rect.height}px`;
    }
  }, [coords]);

  // Escape key + scroll/wheel dismiss
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); }
    };
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('wheel', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('wheel', onScroll, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !coords) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0" style={{ zIndex: ds.zIndex.raw.dropdownBackdrop }} onClick={(e) => { e.stopPropagation(); onClose(); }} />
      {/* Menu panel */}
      <div
        ref={menuRef}
        style={{ top: coords.top, left: coords.left }}
        onClick={(e) => e.stopPropagation()}
        className={[
          `fixed overflow-hidden ${ds.layout.contextMenu.width}`,
          ds.color.fill.elevated,
          ds.radius.xl,
          'border', ds.color.separator.light,
          ds.shadow.menu,
          ds.zIndex.dropdown,
          'px-1.5 py-1.5',
        ].join(' ')}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

/* ─── Menu Item ─────────────────────────────────────────────── */

interface ContextMenuItemProps {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

export function ContextMenuItem({ icon, label, onClick, variant = 'default' }: ContextMenuItemProps) {
  const textColor = variant === 'danger' ? ds.color.label.destructive : ds.color.label.primary;
  const hoverBg = variant === 'danger' ? ds.color.fill.hoverDanger : ds.color.fill.hover;

  return (
    <button
      onClick={onClick}
      className={`${ds.layout.row} ${textColor} ${hoverBg}`}
    >
      {icon}
      <span className={ds.type.body}>{label}</span>
    </button>
  );
}

/* ─── Section Header ────────────────────────────────────────── */

export function ContextMenuSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className={`px-3 pt-3 pb-1 ${ds.layout.sectionHeader} ${ds.color.label.secondary}`}>
      {children}
    </div>
  );
}

/* ─── Divider ───────────────────────────────────────────────── */

export function ContextMenuDivider() {
  return (
    <div className="px-2 py-1.5">
      <div className={`h-px ${ds.color.separator.divider}`} />
    </div>
  );
}
